import React, {useMemo, useState} from 'react';
import {Field, useForm} from 'react-final-form';
import Select from 'react-select';
// The package's nested input/package.json is named "react-phone-number-input/input-min", which trips this rule
// eslint-disable-next-line import/no-extraneous-dependencies
import PhoneInput from 'react-phone-number-input/input';
// Bundled SVG flags: they render the same on every platform (Windows has no flag emoji)
// and aren't hotlinked from a third-party CDN
import flags from 'react-phone-number-input/flags';
import {getLocale} from '@epic-core/common';
import classNames from 'classnames';
import styled from 'styled-components';
import InputLabel from './InputLabel';
import InputValidationErrorMsg from './InputValidationErrorMsg';
import {Styled as SelectStyled, selectAutocompleteStyles} from './CustomSelect.styles';
import {
    CountryOption,
    getCountryOptionLabel,
    getCountryOptions,
    getCountryToSync,
    isRtlLocale,
    phoneNeedsReformat,
    PHONE_COUNTRY_FIELD_NAME,
    required,
    stripDuplicateCallingCode
} from '../../jobForm.utils';

const Styled = styled.div`
    display: flex;
    gap: 16px;

    /* Keep the country select at the reading start, like the rest of the site's RTL sections */
    &.rtl {
        flex-direction: row-reverse;
    }

    .phone-country {
        flex: 0 0 40%;
        min-width: 0;
    }

    .phone-number {
        flex: 1;
        min-width: 0;
    }

    && input:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }

    ${(p) => p.theme.breakpoints.down('xs')} {
        &,
        &.rtl {
            flex-direction: column;
        }
        gap: 0;

        .phone-country {
            flex-basis: auto;
            margin-bottom: 16px;
        }
    }
`;

const OptionLabel = styled.span`
    display: flex;
    align-items: center;
    gap: 8px;
    overflow: hidden;
    white-space: nowrap;

    .phone-country-flag {
        flex: 0 0 auto;
        width: 1.5em;
        height: 1em;
        svg {
            display: block;
            width: 100%;
            height: 100%;
        }
    }

    .phone-country-name {
        overflow: hidden;
        text-overflow: ellipsis;
    }

    .phone-country-code {
        color: #bbb;
    }
`;

const formatCountryOptionLabel = (
    option: CountryOption,
    {context}: {context: 'menu' | 'value'}
): JSX.Element => {
    const Flag = flags[option.value];
    return (
        <OptionLabel>
            {Flag && (
                <span className="phone-country-flag" aria-hidden="true">
                    <Flag title={option.label} />
                </span>
            )}
            {/* The closed select is narrow, so it only shows the flag and calling code */}
            {context === 'menu' && <span className="phone-country-name">{option.label}</span>}
            <span className="phone-country-code">+{option.callingCode}</span>
        </OptionLabel>
    );
};

export interface PhoneFieldProps {
    name: string;
    label?: string;
    isRequired?: boolean;
    isDisabled?: boolean;
    placeholder?: string;
    submitSucceeded?: boolean;
    sectionRequiredLabel?: string;
    getMessage: (key: string) => string;
}

/**
 * Country select plus phone number input. The number can be typed in the selected country's national format
 * or pasted with its own `+` calling code, and is stored in E.164 (e.g. `+14155552671`), so the submitted
 * number always carries its country calling code.
 */
const PhoneField = ({
    name,
    label,
    isRequired,
    isDisabled,
    placeholder,
    submitSucceeded,
    sectionRequiredLabel,
    getMessage
}: PhoneFieldProps): JSX.Element => {
    const form = useForm();
    const locale = getLocale();
    const countryOptions = useMemo(() => getCountryOptions(locale), [locale]);

    // Bumped to remount the number input, which is the only way to make it re-display its value
    const [phoneInputKey, setPhoneInputKey] = useState(0);

    const normalizePhone = (
        typedText: string,
        input: {value?: string; onChange: (value?: string) => void},
        country: string | undefined,
        {reformat}: {reformat: boolean}
    ) => {
        const phone = stripDuplicateCallingCode(input.value, typedText) || input.value;
        if (phone !== input.value) {
            input.onChange(phone);
        }
        // Keep the select in line with a number entered with another calling code
        const countryToSync = getCountryToSync(phone, country);
        if (countryToSync) {
            form.change(PHONE_COUNTRY_FIELD_NAME, countryToSync);
        }
        // A changed value or country re-renders the input on its own. An unchanged one typed as
        // "+1 604 968 1711" would otherwise keep showing the calling code the country select already sets.
        if (reformat && phone === input.value && !countryToSync && phoneNeedsReformat(typedText, phone)) {
            setPhoneInputKey((key) => key + 1);
        }
    };

    return (
        <Styled className={classNames('job-form-phone-field', {rtl: isRtlLocale(locale)})}>
            <div className="phone-country">
                <InputLabel
                    htmlFor={PHONE_COUNTRY_FIELD_NAME}
                    label={getMessage('epic.jobs.form.country')}
                    required={isRequired}
                />
                <Field name={PHONE_COUNTRY_FIELD_NAME} validate={isRequired ? required : undefined}>
                    {({input, meta}) => {
                        const showError = !submitSucceeded && meta.error && meta.touched;
                        const selected = countryOptions.find((option) => option.value === input.value);
                        const countryLabel = getMessage('epic.jobs.form.country');
                        return (
                            <SelectStyled className="custom-select">
                                <div className={classNames({'validate-error': showError})}>
                                    <Select
                                        inputId={PHONE_COUNTRY_FIELD_NAME}
                                        className="dropdown-autocomplete"
                                        styles={selectAutocompleteStyles}
                                        options={countryOptions}
                                        value={selected || null}
                                        // react-select 3 doesn't expose the selected value to screen readers
                                        aria-label={
                                            selected
                                                ? `${countryLabel}: ${getCountryOptionLabel(selected)}`
                                                : countryLabel
                                        }
                                        onChange={(option: CountryOption | null) => {
                                            if (option?.value === input.value) return;
                                            input.onChange(option?.value);
                                            // A number typed for another country won't be valid for this one
                                            form.change(name, undefined);
                                            form.resetFieldState(name);
                                        }}
                                        onBlur={() => input.onBlur()}
                                        getOptionLabel={getCountryOptionLabel}
                                        formatOptionLabel={formatCountryOptionLabel}
                                        isDisabled={isDisabled}
                                        placeholder={getMessage('epic.jobs.form.select')}
                                    />
                                </div>
                                {showError && (
                                    <InputValidationErrorMsg
                                        error={getMessage(meta.error)}
                                        sectionRequiredLabel={sectionRequiredLabel}
                                    />
                                )}
                            </SelectStyled>
                        );
                    }}
                </Field>
            </div>

            <div className="phone-number">
                <InputLabel htmlFor={name} label={label} required={isRequired} />
                <Field name={PHONE_COUNTRY_FIELD_NAME} subscription={{value: true}}>
                    {({input: {value: country}}) => (
                        <Field name={name} validate={isRequired ? required : undefined}>
                            {({input, meta}) => {
                                const showError = !submitSucceeded && meta.error && meta.touched;
                                return (
                                    <>
                                        <PhoneInput
                                            key={phoneInputKey}
                                            id={name}
                                            name={input.name}
                                            type="tel"
                                            autoComplete="tel"
                                            // A default rather than a fixed country, so a pasted or autofilled
                                            // "+49 ..." keeps its own calling code instead of being read as national digits
                                            defaultCountry={country || undefined}
                                            value={input.value || undefined}
                                            onChange={input.onChange}
                                            onBlur={(event) => {
                                                input.onBlur();
                                                normalizePhone(event.currentTarget.value, input, country, {
                                                    reformat: true
                                                });
                                            }}
                                            // Enter submits without blurring, so normalize before the submit too
                                            onKeyDown={(event) => {
                                                if (event.key === 'Enter') {
                                                    // Don't remount the focused input mid-submit
                                                    normalizePhone(event.currentTarget.value, input, country, {
                                                        reformat: false
                                                    });
                                                }
                                            }}
                                            onFocus={input.onFocus}
                                            // The country select sets the calling code, so wait for it
                                            disabled={isDisabled || !country}
                                            placeholder={placeholder}
                                            className={classNames({'validate-error': showError})}
                                        />
                                        {showError && (
                                            <InputValidationErrorMsg
                                                error={getMessage(meta.error)}
                                                sectionRequiredLabel={sectionRequiredLabel}
                                            />
                                        )}
                                    </>
                                );
                            }}
                        </Field>
                    )}
                </Field>
            </div>
        </Styled>
    );
};

export default PhoneField;
